import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PDialogFooterBtnActionType, PDialogService, POkCancelDialogComponent, POkCancelDialogData } from '@frontend-site/ui/components';
import { MfaMethod, UserMfaService, UserMfaState } from '@frontend-site/users/domain';
import { IconName } from '@panaya/ui/p-icon';
import { take } from 'rxjs';
import { AddVerificationMethodDialogComponent } from '../add-verification-method-dialog/add-verification-method-dialog.component';
import { AuthenticatorSetupDialogComponent } from '../authenticator-setup-dialog/authenticator-setup-dialog.component';

export interface SecurityMethodRow {
    method: MfaMethod;
    icon: IconName;
    /** Suffix under `UserProfile.Security.Methods.<KEY>` (Name / Description). */
    key: string;
    isDefault: boolean;
    canMakeDefault: boolean;
    canRemove: boolean;
}

interface MethodCatalogEntry {
    method: MfaMethod;
    icon: IconName;
    key: string;
    removable: boolean;
}

/**
 * The "Security" tab of the profile dialog: password row plus the verification-methods table.
 * Owns the MFA state and the enroll / remove dialogs (those commit immediately on the server);
 * the *default* method and the password are only staged here and saved by the profile dialog.
 */
@Component({
    selector: 'p-user-security-tab',
    templateUrl: './user-security-tab.component.html',
    styleUrls: ['./user-security-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserSecurityTabComponent implements OnInit {
    /** Methods a user can add from the profile. Passkey joins once its WebAuthn flow ships. */
    static readonly ENROLLABLE_METHODS: readonly MfaMethod[] = [MfaMethod.APP];

    private static readonly CATALOG: readonly MethodCatalogEntry[] = [
        { method: MfaMethod.EMAIL, icon: 'mail', key: 'EMAIL', removable: false },
        { method: MfaMethod.APP, icon: 'smartphone', key: 'APP', removable: true },
        { method: MfaMethod.PASSKEY, icon: 'key', key: 'PASSKEY', removable: true }
    ];

    @Input() companyId?: number;
    /** The default the user picked but has not saved yet; null means "as on the server". */
    @Input() stagedDefault: MfaMethod | null = null;
    @Output() changePassword = new EventEmitter<void>();
    @Output() makeDefault = new EventEmitter<MfaMethod>();
    /** Fired whenever the server-side state was (re)loaded, so the parent can reconcile what it staged. */
    @Output() stateChange = new EventEmitter<UserMfaState>();

    mfaState: UserMfaState | null = null;
    /** Shown in place of the methods when the state can't be loaded (e.g. the identity provider is unreachable). */
    loadError = '';

    constructor(
        private readonly userMfaService: UserMfaService,
        private readonly dialog: PDialogService,
        private readonly translate: TranslateService,
        private readonly cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.loadError = '';
        this.userMfaService
            .getState(this.companyId)
            .pipe(take(1))
            .subscribe({
                next: (state) => this.applyState(state),
                error: (err) => {
                    this.loadError = err?.error?.error || 'UserProfile.Security.unavailable';
                    this.cd.markForCheck();
                }
            });
    }

    get rows(): SecurityMethodRow[] {
        if (!this.mfaState) {
            return [];
        }
        const enrolled = this.mfaState.enrolledMethods;
        const effectiveDefault = this.effectiveDefault(enrolled);
        return UserSecurityTabComponent.CATALOG.filter((entry) => enrolled.includes(entry.method)).map((entry) => {
            const isDefault = entry.method === effectiveDefault;
            return {
                method: entry.method,
                icon: entry.icon,
                key: entry.key,
                isDefault,
                canMakeDefault: !isDefault,
                canRemove: entry.removable
            };
        });
    }

    get canAddMethod(): boolean {
        if (!this.mfaState) {
            return false;
        }
        const enrolled = this.mfaState.enrolledMethods;
        return UserSecurityTabComponent.ENROLLABLE_METHODS.some((m) => !enrolled.includes(m));
    }

    trackByMethod(_: number, row: SecurityMethodRow): MfaMethod {
        return row.method;
    }

    onAddMethod(): void {
        const enrolled = this.mfaState?.enrolledMethods ?? [];
        const candidates = UserSecurityTabComponent.ENROLLABLE_METHODS.filter((m) => !enrolled.includes(m));
        // Only one method to add (today, always APP) → skip the picker and jump straight to its setup.
        // Once PASSKEY ships we'll have 2+ candidates and the picker shows again.
        if (candidates.length === 1 && candidates[0] === MfaMethod.APP) {
            this.openAppSetup();
            return;
        }
        const data = { companyId: this.companyId, enrolledMethods: enrolled };
        this.dialog
            .open(AddVerificationMethodDialogComponent, { panelClass: 'p-dialog-small', data })
            .afterClosed()
            .pipe(take(1))
            .subscribe((state?: UserMfaState) => {
                if (state) {
                    this.applyState(state);
                }
            });
    }

    private openAppSetup(): void {
        this.dialog
            .open(AuthenticatorSetupDialogComponent, { panelClass: 'p-dialog-small', data: { companyId: this.companyId } })
            .afterClosed()
            .pipe(take(1))
            .subscribe((state?: UserMfaState) => {
                if (state) {
                    this.applyState(state);
                }
            });
    }

    onRemove(method: MfaMethod): void {
        const key = UserSecurityTabComponent.CATALOG.find((e) => e.method === method)?.key ?? '';
        const data: POkCancelDialogData = {
            headerText: this.translate.instant('UserProfile.Security.RemoveMethod.header'),
            headerIcon: 'alert-triangle',
            headerIconColor: 'status-default-medium',
            bodyText: this.translate.instant('UserProfile.Security.RemoveMethod.body', {
                method: this.translate.instant(`UserProfile.Security.Methods.${key}.Name`)
            }),
            okText: 'UserProfile.Security.RemoveMethod.confirm',
            okButtonActionType: PDialogFooterBtnActionType.Destructive,
            hideCancelButton: false
        };
        this.dialog
            .open(POkCancelDialogComponent, { panelClass: 'p-dialog-small', data })
            .afterClosed()
            .pipe(take(1))
            .subscribe((confirmed?: boolean) => {
                if (confirmed === true) {
                    this.userMfaService
                        .removeMethod(method, this.companyId)
                        .pipe(take(1))
                        .subscribe((state) => this.applyState(state));
                }
            });
    }

    private applyState(state: UserMfaState): void {
        // Only the initial GET returns lastPasswordChange; confirm/remove omit it (the password hasn't
        // moved), so preserve what we already have rather than clobbering it to null in the UI.
        const merged: UserMfaState = { ...state, lastPasswordChange: state.lastPasswordChange ?? this.mfaState?.lastPasswordChange };
        this.mfaState = merged;
        this.stateChange.emit(merged);
        this.cd.markForCheck();
    }

    private effectiveDefault(enrolled: MfaMethod[]): MfaMethod | null {
        if (this.stagedDefault != null && enrolled.includes(this.stagedDefault)) {
            return this.stagedDefault;
        }
        return this.mfaState?.defaultMethod ?? null;
    }
}
